import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Link2, Copy, RefreshCw, Trash2, Plus, ExternalLink, Loader2, Camera, Coins, Search,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_ORIGIN = window.location.origin;

const PUBLIC_PATH = { cash_close: '/cash-close', scan: '/scan' };
const SCOPE_LABEL = { cash_close: 'Cash-close', scan: 'Scan' };

const StatePill = ({ live }) => (
  <span
    className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
    style={{
      background: live ? '#E7F2EC' : '#F4F0E6',
      color: live ? '#2F5D48' : '#8B8377',
    }}
    data-testid={live ? 'link-state-live' : 'link-state-none'}
  >
    {live ? 'Live' : 'None'}
  </span>
);

const LinkCell = ({ row, scope, onChanged }) => {
  const token = row[scope];
  const live = Boolean(token);
  const url = live ? `${SITE_ORIGIN}${PUBLIC_PATH[scope]}/${token.token}` : '';
  const [busy, setBusy] = useState(false);

  const issue = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/${scope === 'scan' ? 'scan' : 'cash-close'}/tokens/${row.client_id}?label=shift`);
      toast.success(`${SCOPE_LABEL[scope]} link issued for ${row.name}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to issue link');
    } finally { setBusy(false); }
  };
  const rotate = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/${scope === 'scan' ? 'scan' : 'cash-close'}/tokens/${row.client_id}/rotate?label=shift`);
      toast.success(`${SCOPE_LABEL[scope]} link rotated — old URL will stop working`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to rotate');
    } finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!window.confirm(`Revoke ${SCOPE_LABEL[scope]} link for ${row.name}? The manager's URL will stop resolving.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/${scope === 'scan' ? 'scan' : 'cash-close'}/tokens/${row.client_id}/${token.id}`);
      toast.success(`${SCOPE_LABEL[scope]} link revoked`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to revoke');
    } finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success('Link copied'); }
    catch { toast.error('Clipboard unavailable — select the URL manually'); }
  };

  if (!live) {
    return (
      <button
        onClick={issue}
        disabled={busy}
        className="inline-flex items-center gap-1 text-xs text-[#2F5D48] hover:text-[#B06A2C] disabled:opacity-50"
        data-testid={`issue-${scope}-${row.client_id}`}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
        Issue {SCOPE_LABEL[scope]} link
      </button>
    );
  }
  return (
    <div className="flex items-center justify-between gap-2 group">
      <div className="min-w-0 flex-1">
        <StatePill live />
        <div className="text-[10px] text-text-muted mt-0.5 font-mono truncate" title={url}>
          {url.replace(SITE_ORIGIN, '')}
        </div>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
        <button onClick={copy} title="Copy URL" className="p-1.5 rounded hover:bg-[#F4F0E6]" data-testid={`copy-${scope}-${row.client_id}`}>
          <Copy className="h-3.5 w-3.5 text-[#6E5334]" />
        </button>
        <a href={url} target="_blank" rel="noreferrer" title="Open" className="p-1.5 rounded hover:bg-[#F4F0E6]">
          <ExternalLink className="h-3.5 w-3.5 text-[#6E5334]" />
        </a>
        <button onClick={rotate} disabled={busy} title="Rotate (invalidate current URL)" className="p-1.5 rounded hover:bg-[#F4F0E6] disabled:opacity-50" data-testid={`rotate-${scope}-${row.client_id}`}>
          <RefreshCw className="h-3.5 w-3.5 text-[#B06A2C]" />
        </button>
        <button onClick={revoke} disabled={busy} title="Revoke" className="p-1.5 rounded hover:bg-red-50 disabled:opacity-50" data-testid={`revoke-${scope}-${row.client_id}`}>
          <Trash2 className="h-3.5 w-3.5 text-[#8B4B24]" />
        </button>
      </div>
    </div>
  );
};

export const LinksAdmin = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/links`);
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load links');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) =>
    !query.trim() || r.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="links-admin-page">
      <div className="mb-6">
        <div className="text-[11px] tracking-[.18em] text-[#6E5334] uppercase">Admin</div>
        <h1 className="text-3xl font-semibold text-[#211C16] mt-1" style={{ fontFamily: 'Fraunces, serif' }}>Links</h1>
        <p className="text-sm text-text-secondary mt-2 max-w-2xl">
          One row per client. Each has two phone-first public links — the nightly cash-close form and the Z-report / settlement-slip scanner. Hover a live row to copy, rotate or revoke.
        </p>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="h-4 w-4 absolute left-2 top-2.5 text-[#8B8377]" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter clients"
            className="w-full border border-[#EDE5D3] rounded-md pl-8 pr-3 py-2 text-sm bg-white"
            data-testid="links-filter"
          />
        </div>
        <div className="text-[11px] text-text-muted">{filtered.length} client{filtered.length === 1 ? '' : 's'}</div>
      </div>

      <div className="bg-white border border-[#EDE5D3] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FBF8F0]">
            <tr className="text-left text-[10px] uppercase tracking-[.12em] text-[#6E5334]">
              <th className="py-3 px-4">Client</th>
              <th className="py-3 px-4">
                <div className="flex items-center gap-1.5"><Coins className="h-3.5 w-3.5" />Cash-close link</div>
              </th>
              <th className="py-3 px-4">
                <div className="flex items-center gap-1.5"><Camera className="h-3.5 w-3.5" />Scan link</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && filtered.length === 0 ? (
              <tr><td colSpan={3} className="py-6 text-center text-text-muted"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={3} className="py-8 text-center text-text-muted italic">No clients found.</td></tr>
            ) : filtered.map((r) => (
              <tr key={r.client_id} className="border-t border-dashed border-[#EDE5D3]" data-testid={`links-row-${r.client_id}`}>
                <td className="py-3 px-4 font-medium text-[#211C16]">{r.name}</td>
                <td className="py-3 px-4 min-w-[280px]"><LinkCell row={r} scope="cash_close" onChanged={load} /></td>
                <td className="py-3 px-4 min-w-[280px]"><LinkCell row={r} scope="scan" onChanged={load} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LinksAdmin;
